import Vue from "vue";
import axios from "axios";
import VueAxios from "vue-axios";
import 'url-search-params-polyfill';
import {API_URL} from "@/common/config";
import {authHandler, checkResponseStatus} from "../main";

const ApiService = {
    init() {
        Vue.use(VueAxios, axios);
        Vue.axios.defaults.baseURL = API_URL;
        Vue.axios.interceptors.response.use(
            response => checkResponseStatus(response),
            error => authHandler(error)
        )
    },

    query(resource, params) {
        return Vue.axios.get(resource, params).catch(error => {
            throw new Error(`ApiService ${error}`);
        });
    },

    get(resource) {
        return Vue.axios.get(resource).catch(error => {
            throw new Error(`ApiService ${error}`);
        });
    },

    post(resource, params) {
        return Vue.axios.post(`${resource}`, params);
    },

    update(resource, slug, params) {
        return Vue.axios.put(`${resource}/${slug}`, params);
    },

    put(resource, params) {
        return Vue.axios.put(`${resource}`, params);
    },

    delete(resource) {
        return Vue.axios.delete(resource).catch(error => {
            throw new Error(`ApiService ${error}`);
        });
    },

    fileDownload(resource, params) {
        let configWithParams = params;
        configWithParams.responseType = "blob";

        return Vue.axios.get(resource, configWithParams).catch(error => {
            throw new Error(`ApiService ${error}`);
        });
    },

    postMultiPartForm(resource, params, files) {
        const config = {headers: {'content-type': 'multipart/form-data'}};
        const formData = new FormData();
        for (let field in params) {
            if (params.hasOwnProperty(field)) {
                formData.append(`${field}`, params[field])
            }
        }

        for (let i = 0; i < files.length; i++) {
            formData.append('files[' + i + ']', files[i]);
        }
        return Vue.axios.post(`${resource}`, formData, config);
    }
};

export default ApiService;

export const ParametricSearchService = {

    getCategories() {
        return ApiService.get("parametricsearch/v2/categories");
    },

    query(params) {
        return ApiService.query("parametricsearch/v2/search", {
            params: params
        });
    },

    downloadExcel(params) {
        return ApiService.fileDownload("parametricsearch/v2/excel", {
            params: params
        });
    },
    compareProductQuery(params) {
        return ApiService.query("parametricsearch/v2/compare", {
            params: params
        });
    },
    recommendedDriverQuery(params) {
        return ApiService.query("parametricsearch/v2/recommended-driver", {
            params: params
        });
    },
    demoBoardQuery(params) {
        return ApiService.query("parametricsearch/v2/demo-board", {
            params: params
        });
    },
    downloadDemoBoardExcel(params) {
        return ApiService.fileDownload("parametricsearch/v2/demo-board/excel", {params: {typeNumber: params}});
    }
};

export const UserService = {

    getCountries() {
        return ApiService.query("country/v1/countries");
    },

    getCurrentUser() {
        return ApiService.get("user-management/v1/user/current")
    },

    loginUser(params) {
        return ApiService.post("user-management/v1/login", params);
    },

    registerUser(params) {
        return ApiService.post("user-management/v1/register", params);
    },

    approveUser(params) {
        return ApiService.put("user-management/v1/approve", params);
    },

    activateUser(params) {
        return ApiService.put("user-management/v1/activate", params);
    },

    forgotPassword(params) {
        return ApiService.post("user-management/v1/forgot-password", params);
    },

    resetPassword(params) {
        return ApiService.put("user-management/v1/reset-password", params);
    },

    isPasswordNew(params) {
        return ApiService.put("user-management/v1/is-password-new", params);
    },

    resetExpiredPassword(params) {
        return ApiService.put("user-management/v1/reset-expired-password", params);
    },

    validatePasswordToken(params) {
        return ApiService.query("user-management/v1/validate-token", {
            params: {
                email: params.email,
                token: params.token
            }
        });
    },

    getUserAccount(email) {
        return ApiService.query(`user-management/v1/users/${email}`);
    },
    updateUserAccount(params, email) {
        return ApiService.put(`user-management/v1/users/${email}`, params);
    },
    updateUserSubscriptions(params, email) {
        return ApiService.put(`user-management/v1/users/${email}/subscriptions`, params);
    },
    deleteUserAccount(email) {
        return ApiService.delete(`user-management/v1/users/${email}`);
    }
};

export const SupportService = {
    registrationLoginSupport(params, files) {
        return ApiService.postMultiPartForm("support/v1/registration-login-support", params, files);
    }
};

export const MyDocumentsService = {

    getCategories() {
        return ApiService.get(`documentlibrary/v2/categories`);
    },

    getDocuments(params) {
        return ApiService.query("documentlibrary/v2/documents", {params: params});
    }
};

export const TasksService = {

    getTasks(params) {
        return ApiService.query("/tasks/v1/tasks", {params: params})
    },

    getUserGroups(params) {
        return ApiService.query("tasks/v1/user-groups", {params: {email: params.email}});
    },

    getDocumentGroups(params) {
        return ApiService.query("tasks/v1/document-groups", {params: {documentId: params.documentId}});
    },

    getProducts(query) {
        return ApiService.query("tasks/v1/products", {params: {name: query}});
    },

    getProductsForDocument(params) {
        return ApiService.query("tasks/v1/products-for-document", {params: {documentId: params.documentId}});
    },

    approveDocument(taskId, params) {
        return ApiService.put(`tasks/v1/document-approval-tasks/${taskId}`, params);
    },

    rejectDocument(taskId, comment) {
        return ApiService.delete(`tasks/v1/document-approval-tasks/${taskId}?&comment=${comment}`);
    },

};

export const ProductsService = {

    getLatestProducts(params) {
        return ApiService.query("product/v1/products", {params: params});
    },

    getProductCategories() {
        return ApiService.get("product/v1/categories");
    }

};


export const MySupportService = {
    sendSupportTicket(params, files) {
        return ApiService.postMultiPartForm("support/v1/my-support", params, files);
    },
    getContact() {
        return ApiService.get(`support/v1/account-manager`);
    },
    getTopics() {
        return ApiService.get(`support/v1/topics`);
    }
};
